/* eslint camelcase: "off" */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  DismissibleTag,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jem-open/jem-ui";
import {
  useIntegrationsNotificationConfig,
  useIntegrationsRecipientCandidates,
  useUpdateIntegrationsNotificationConfig,
} from "../../services/api/IntegrationsNotificationConfigApiService";
import {
  IntegrationsNotificationSchedule,
  IntegrationsRecipientCandidate,
} from "../../services/model/IntegrationsNotificationConfig";

const NONE_VALUE = "NONE";

const SCHEDULE_OPTIONS: { value: string; label: string }[] = [
  { value: NONE_VALUE, label: "No schedule" },
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const toSelectValue = (schedule: IntegrationsNotificationSchedule): string =>
  schedule ?? NONE_VALUE;

const toScheduleValue = (value: string): IntegrationsNotificationSchedule => {
  if (value === "DAILY" || value === "WEEKLY" || value === "MONTHLY") {
    return value;
  }
  return null;
};

const candidateLabel = (candidate: IntegrationsRecipientCandidate): string => {
  const fullName = `${candidate.first_name} ${candidate.last_name}`.trim();
  return fullName ? `${fullName} <${candidate.email}>` : candidate.email;
};

const arraysEqualUnordered = <T,>(
  a: T[],
  b: T[],
  key: (value: T) => string | number = (value) => value as unknown as string | number
): boolean => {
  if (a.length !== b.length) return false;
  const setA = new Set(a.map(key));
  return b.every((value) => setA.has(key(value)));
};

const IntegrationsReportScheduling: React.FC = () => {
  const { data, isLoading, isError } = useIntegrationsNotificationConfig();
  const candidatesQuery = useIntegrationsRecipientCandidates();
  const update = useUpdateIntegrationsNotificationConfig();
  const { reset: resetUpdate, mutate: saveConfig } = update;

  const [selected, setSelected] = useState<string>(NONE_VALUE);
  const [pickedUserIds, setPickedUserIds] = useState<number[]>([]);
  const [extraEmails, setExtraEmails] = useState<string[]>([]);
  const [emailDraft, setEmailDraft] = useState<string>("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (data && !initializedRef.current) {
      setSelected(toSelectValue(data.schedule));
      setPickedUserIds(data.recipient_user_ids ?? []);
      setExtraEmails(data.recipient_emails ?? []);
      initializedRef.current = true;
    }
  }, [data]);

  useEffect(() => {
    resetUpdate();
  }, [selected, pickedUserIds, extraEmails, resetUpdate]);

  const isDirty = useMemo(() => {
    if (!data) return false;
    if (toSelectValue(data.schedule) !== selected) return true;
    if (!arraysEqualUnordered(data.recipient_user_ids ?? [], pickedUserIds)) {
      return true;
    }
    if (
      !arraysEqualUnordered(
        data.recipient_emails ?? [],
        extraEmails,
        (email) => email.toLowerCase()
      )
    ) {
      return true;
    }
    return false;
  }, [data, selected, pickedUserIds, extraEmails]);

  const togglePickedUser = (id: number, checked: boolean): void => {
    setPickedUserIds((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }
      return prev.filter((existing) => existing !== id);
    });
  };

  const addEmail = (): void => {
    const trimmed = emailDraft.trim().toLowerCase();
    if (!trimmed) return;
    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError("Enter a valid email address.");
      return;
    }
    if (extraEmails.includes(trimmed)) {
      setEmailError("Email already added.");
      return;
    }
    setExtraEmails((prev) => [...prev, trimmed]);
    setEmailDraft("");
    setEmailError(null);
  };

  const removeEmail = (email: string): void => {
    setExtraEmails((prev) => prev.filter((existing) => existing !== email));
  };

  const handleEmailKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addEmail();
    }
  };

  const handleSave = (): void => {
    saveConfig({
      schedule: toScheduleValue(selected),
      recipient_user_ids: pickedUserIds,
      recipient_emails: extraEmails,
    });
  };

  if (isError && !data) {
    return (
      <div className="mt-4">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load notification schedule.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const candidates = candidatesQuery.data ?? [];
  const recipientsEmpty = pickedUserIds.length === 0 && extraEmails.length === 0;

  return (
    <div className="mt-4 max-w-md flex flex-col gap-md">
      {isError && data && (
        <Alert>
          <AlertDescription>
            Could not refresh the schedule. Showing the last known value.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-xs">
        <Label htmlFor="integrations-schedule">Report schedule</Label>
        <p className="text-sm text-greyscale-text-body">
          How often Jem should email the integrations health report.
        </p>
        <Select
          value={selected}
          onValueChange={setSelected}
          disabled={isLoading || update.isLoading}
        >
          <SelectTrigger id="integrations-schedule">
            <SelectValue placeholder="Select schedule" />
          </SelectTrigger>
          <SelectContent>
            {SCHEDULE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-xs">
        <Label>Recipients (users)</Label>
        <p className="text-sm text-greyscale-text-body">
          Pick teammates who should receive the report.
        </p>
        {candidatesQuery.isLoading && (
          <p className="text-sm text-greyscale-text-body">Loading users…</p>
        )}
        {candidatesQuery.isError && (
          <Alert variant="destructive">
            <AlertDescription>Failed to load user list.</AlertDescription>
          </Alert>
        )}
        {!candidatesQuery.isLoading && !candidatesQuery.isError && candidates.length === 0 && (
          <p className="text-sm text-greyscale-text-body">
            No eligible users for this employer.
          </p>
        )}
        {candidates.length > 0 && (
          <div className="flex flex-col gap-xxs max-h-64 overflow-y-auto border rounded p-2">
            {candidates.map((candidate) => {
              const checked = pickedUserIds.includes(candidate.id);
              return (
                <label
                  key={candidate.id}
                  className="flex items-center gap-sm cursor-pointer"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value: boolean | "indeterminate") =>
                      togglePickedUser(candidate.id, value === true)
                    }
                  />
                  <span className="text-sm">{candidateLabel(candidate)}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-xs">
        <Label htmlFor="integrations-extra-email">Additional emails</Label>
        <p className="text-sm text-greyscale-text-body">
          Add external addresses (finance ops, shared inboxes).
        </p>
        <div className="flex gap-xs">
          <Input
            id="integrations-extra-email"
            type="email"
            value={emailDraft}
            placeholder="finance@example.com"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setEmailDraft(event.target.value);
              if (emailError) setEmailError(null);
            }}
            onKeyDown={handleEmailKeyDown}
            disabled={update.isLoading}
          />
          <Button
            variant="secondary"
            onClick={addEmail}
            disabled={!emailDraft.trim() || update.isLoading}
          >
            Add
          </Button>
        </div>
        {emailError && (
          <p className="text-sm text-destructive">{emailError}</p>
        )}
        {extraEmails.length > 0 && (
          <div className="flex flex-wrap gap-xs">
            {extraEmails.map((email) => (
              <DismissibleTag
                key={email}
                onDismiss={() => removeEmail(email)}
              >
                {email}
              </DismissibleTag>
            ))}
          </div>
        )}
      </div>

      {recipientsEmpty && (
        <Alert>
          <AlertDescription>
            No recipients configured — the report will fall back to all admins.
          </AlertDescription>
        </Alert>
      )}

      {update.isError && (
        <Alert variant="destructive">
          <AlertDescription>Failed to save settings.</AlertDescription>
        </Alert>
      )}
      {update.isSuccess && !isDirty && (
        <Alert>
          <AlertDescription>Settings saved.</AlertDescription>
        </Alert>
      )}

      <div>
        <Button
          onClick={handleSave}
          loading={update.isLoading}
          disabled={isLoading || !isDirty}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default IntegrationsReportScheduling;
